import React from 'react';
import cn from 'classnames';
import { BadgeColor, getFaIcon, Icon } from '@odin-labs/components';
import {
  JobsiteAccessEvent,
  AccessEventCrossingKind,
  AccessEventEndpointKind,
  JobsiteAccessEventStatus,
  JobsiteWorkerCheckInLocationStatus,
} from 'apollo/generated/client-operations';
import { faArrowToLeft, faArrowToRight, fasMoonStars } from 'components/icons';

export const statusText: Record<JobsiteAccessEventStatus, string> = {
  Admit: 'Admitted',
  Reject: 'Rejected',
  archived: 'Archived',
};
export const statusColor: Record<JobsiteAccessEventStatus, BadgeColor> = {
  Admit: 'green',
  Reject: 'red',
  archived: 'gray',
};

export const locationStatusText: Record<
  JobsiteWorkerCheckInLocationStatus,
  string
> = {
  LocationNotAvailable: 'Location Not Available',
  Nearby: 'Nearby',
  Offsite: 'Offsite',
  Onsite: 'Onsite',
};

export const locationStatusColor: Record<
  JobsiteWorkerCheckInLocationStatus,
  BadgeColor
> = {
  LocationNotAvailable: 'gray',
  Nearby: 'yellow',
  Offsite: 'red',
  Onsite: 'green',
};

const directionIcon: Record<string, Icon> = {
  inbound: getFaIcon({
    icon: faArrowToRight,
    className: 'odin-text-odin-primary',
  }),
  outbound: getFaIcon({ icon: faArrowToLeft, className: 'odin-text-gray-600' }),
};

const classes = {
  defaultText: cn(
    'odin-text-gray-900 odin-text-sm odin-leading-5 odin-font-normal',
  ),
  jobsite: cn(
    'odin-text-gray-900 odin-text-sm odin-leading-5 odin-font-medium',
  ),
  date: cn('odin-text-gray-900 odin-text-sm odin-leading-5 odin-font-normal'),
  supportingText: cn(
    'odin-text-gray-600 odin-text-xs odin-leading-4 odin-font-normal',
  ),
  directionGatewayText: cn(
    'odin-text-gray-900 odin-text-sm odin-leading-5 odin-font-normal odin-ml-1.5',
  ),
};

export type DateTimeCellData = {
  date: Date;
  timeZoneName?: string;
  isOvernight?: boolean;
};

export type SessionDateCellData = {
  date: string;
  isOvernight?: boolean;
};

export function JobsiteCell({ value }: { value: string }): React.ReactElement {
  return <div className={classes.jobsite}>{value}</div>;
}

const OvernightIcon = getFaIcon({
  icon: fasMoonStars,
  title: 'Overnight session',
});

export function DateTimeCell({
  value,
}: {
  value: DateTimeCellData;
}): React.ReactElement {
  return (
    <div className="odin-flex odin-items-baseline odin-gap-x-1 md:odin-block">
      <div className={classes.date}>
        {value?.date?.toLocaleDateString()}
        {value?.isOvernight && (
          <OvernightIcon className="odin-ml-3 odin-text-odin-primary" />
        )}
      </div>
      <div className={classes.supportingText}>
        {value?.date?.toLocaleTimeString()} {value?.timeZoneName}
      </div>
    </div>
  );
}
export function SessionDateCell({
  value,
}: {
  value: SessionDateCellData;
}): React.ReactElement {
  return (
    <div className={classes.date}>
      {value?.date}
      {value?.isOvernight && (
        <OvernightIcon className="odin-ml-3 odin-text-odin-primary" />
      )}
    </div>
  );
}

const crossingMovement = (
  crossing: JobsiteAccessEvent['zoneCrossing'],
): string | undefined => {
  if (!crossing) return undefined;
  if (crossing.kind === AccessEventCrossingKind.Unresolved)
    return 'Zone mapping unavailable';
  const endpointName = (
    endpoint: NonNullable<JobsiteAccessEvent['zoneCrossing']>['from'],
  ): string =>
    endpoint?.kind === AccessEventEndpointKind.Outside
      ? 'Outside site'
      : endpoint?.name ?? 'Unknown zone';
  return `${endpointName(crossing.from)} → ${endpointName(crossing.to)}`;
};

export function DirectionGatewayCell({
  value,
}: {
  value: JobsiteAccessEvent;
}): React.ReactElement {
  const DirectionIcon = directionIcon[value.direction];
  const crossing = value.zoneCrossing;
  const movement = crossingMovement(crossing);
  return (
    <div>
      {DirectionIcon && (
        <DirectionIcon className="odin-text-base odin-font-light" />
      )}
      <span className={classes.directionGatewayText}>{value.gateway}</span>
      {movement && (
        <div className={classes.supportingText}>
          {value.status === JobsiteAccessEventStatus.Reject &&
          crossing?.kind !== AccessEventCrossingKind.Unresolved
            ? 'Attempted: '
            : ''}
          {movement}
        </div>
      )}
    </div>
  );
}

export function JobsiteAccessDefaultCell({
  value,
}: {
  value: string;
}): React.ReactElement {
  return <span className={classes.defaultText}>{value}</span>;
}
